/**
 * Pure gating predicate for the "Extract booking" attachment action —
 * ADR-0081 slice 3.
 *
 * Lives in its own module (separate from AttachmentsSection.tsx) so
 * the vitest test suite can import it without dragging in
 * `react-native` — the component imports Pressable which Vite/rolldown
 * can't parse in the SSR test environment.
 *
 * The component renders the action whenever this returns true.
 */

import type { Attachment } from '@my-fullstack-app/itinerary-types';
import { getDocumentSchema } from '@my-fullstack-app/itinerary-types';
import type { AiQuotaBucket } from '@my-fullstack-app/trpc-types';
import { isParserEligibleMime } from '@my-fullstack-app/trpc-types';

/** All gating conditions from ADR-0081 Q5 lined up here as the
 *  single source of truth. The quota check is two parts:
 *    - `cap > 0` means the feature is available to this tier / the
 *      kill-switch is on.
 *    - `used < cap` means the user still has slots left. Tapping when
 *      exhausted would only land them on a QUOTA_EXCEEDED error card
 *      after a wasted server round-trip and a debit write — hide the
 *      action client-side instead.
 *
 *  ADR-0083: labels with a registered `DocumentSchema` (passport /
 *  visa / insurance / vaccination / future schema-driven labels)
 *  route to the vault extractor instead — gating those is
 *  `canExtractVaultDocument`'s job. Excluding them here makes the
 *  two predicates mutually exclusive at the label level, so the
 *  visibility union matches the server's label-based dispatch and a
 *  passport-labelled .eml doesn't surface a button that always 500s. */
export function canExtractBookingFromAttachment(
  attachment: Attachment,
  opts: {
    canEdit: boolean;
    parseQuotaStatus: AiQuotaBucket | null | undefined;
  }
): boolean {
  if (!opts.canEdit) return false;
  const quota = opts.parseQuotaStatus;
  if (!quota || quota.cap <= 0) return false;
  if (quota.used >= quota.cap) return false;
  if (attachment.state !== 'uploaded') return false;
  if (!isParserEligibleMime(attachment.mimeType)) return false;
  if (getDocumentSchema(attachment.label)) return false;
  return true;
}
